import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useTimeoutFn } from '@sorare/core/src/hooks/animations/useTimeoutFn';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import type {
  PrimaryOfferBuyerQuery,
  PrimaryOfferBuyerQueryVariables,
} from './__generated__/usePollPrimaryOfferBuyer.graphql.ts';

const PRIMARY_OFFER_BUYER_QUERY = gql`
  query PrimaryOfferBuyerQuery($id: String!) {
    tokens {
      primaryOffer(id: $id) {
        id
        buyer {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<PrimaryOfferBuyerQuery, PrimaryOfferBuyerQueryVariables>;

const POLL_INTERVAL = 1000;
const POLL_TIMEOUT = 10000;

export const usePollPrimaryOfferBuyer = ({
  primaryOfferId,
  onBought,
  onEnd,
}: {
  primaryOfferId: string;
  onBought: () => void;
  onEnd: () => void;
}) => {
  const { currentUser } = useCurrentUserContext();
  const { data } = useQuery(PRIMARY_OFFER_BUYER_QUERY, {
    variables: { id: idFromObject(primaryOfferId) },
    pollInterval: POLL_INTERVAL,
  });

  useTimeoutFn(onEnd, POLL_TIMEOUT);

  const buyerSlug = data?.tokens?.primaryOffer?.buyer?.slug;
  const userIsBuyer = !!buyerSlug && buyerSlug === currentUser?.slug;

  useEffect(() => {
    if (userIsBuyer) {
      onBought();
      onEnd();
    }
  }, [userIsBuyer, onBought, onEnd]);
};
